import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Route, Router } from '@angular/router';
import { AppComponent } from 'src/app/app.component';
import { MobileNavBaseComponent } from 'src/app/base/components/mobile-nav-base.component';
import { TopNavBaseComponent } from 'src/app/base/components/top-nav-base.component';
import { AuthService } from 'src/app/base/services/auth/auth.service';
import { NavService } from 'src/app/base/services/nav/nav.service';
import { SidenavComponent } from 'src/app/gigmonster/components/sidenav/sidenav.component';
import { PsuedoLoginPageInformation } from '../easysuite-superstructure/easysuite-superstructure.component';
import { findFirstPageLink, buildNavArray } from '../../../base/helpers';

@Component({
    selector: 'app-easysuite-mobile-nav',
    templateUrl: './easysuite-mobile-nav.component.html',
    styleUrls: ['./easysuite-mobile-nav.component.css']
})

export class EasysuiteMobileNavComponent extends MobileNavBaseComponent implements OnInit {
    @Input() psuedoLoginInfo: PsuedoLoginPageInformation = null;

    data: any = [];
    showMobileNav: boolean = false;
    currentRoutePath: string = '';

    constructor(public sideNav: SidenavComponent,
        public http: HttpClient,
        public router: Router,
        public navService: NavService,
        public authService: AuthService,
        public activated: ActivatedRoute,
        public app: AppComponent,
        public activatedRoute: ActivatedRoute) {
        super(router, navService, authService);
    }

    ngOnInit(): void {
        this.data = [];
        this.currentRoutePath = this.router.url;

        // Listen for route changes to update the current route path
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.currentRoutePath = event.url;
                this.expandActiveRouteParents();
            }
        });

        let filteredRoutes = this.router.config.filter(x => x.path.split("/").length == 1 && x.path != "auth" && x.path != "**");

        filteredRoutes.forEach(x => {
            this.data.push(x);
        });

        this.data = this.data.sort(this.asIsOrder);

        this.data.forEach(route => {
            route.childNavs = buildNavArray(this.router.config, route.path, 0);
            
            // Create the correct link for parent routes
            let link = "/" + route.path;

            // Parent nav has no page; search recursively for the first valid descendant's link.
            if (!route.data?.hasPage && route.childNavs && route.childNavs.length > 0) {
                const validLink = findFirstPageLink(route.childNavs);
                if (validLink) {
                    link = validLink;
                }
            }
            route.link = link;
        });

        // Expand the active route and its parents
        this.expandActiveRouteParents();
    }

    // Expand the current route and its parent routes
    expandActiveRouteParents(): void {
        this.data.forEach(route => {

            // The parent route should expand only if a child is active
            const hasActiveChild = this.expandRouteAndParents(route.childNavs, this.currentRoutePath);

            // Or if the parent route is the current route
            const isRootActive = this.currentRoutePath === '/' + route.path || this.currentRoutePath.startsWith('/' + route.path + '/');
            route.collapsed = !(hasActiveChild || isRootActive);
        });
    }

    // Recursive function to expand the current route and its parents
    expandRouteAndParents(navItems: any[], currentPath: string): boolean {
        let foundActive = false;

        if (!navItems) return false;

        for (const item of navItems) {

            // Match the current path with the item's link
            const isExact = currentPath === item.link || currentPath.startsWith(item.link + '/');
            const childActive = item.children ? this.expandRouteAndParents(item.children, currentPath) : false;

            if (isExact || childActive) {

                // Just expand the item if it matches the current path or has an active child
                item.collapsed = false; 
                foundActive = true;

            // If we found the path, we don't keep expanding others
            } else {

                // Collapse those that are not part of the active path
                item.collapsed = true; 
            }
        }

        return foundActive;
    }

    isActive(link): boolean {
        return this.router.url.split("/")[1] == link.split("/")[0];
    }

    /**
     * - Some parent nav items do not have their own page (e.g. Messages, Communication).
     *   In those cases, their `link` points to the first child.
     * - To fix this, use `originalLink`, which always represents the parent's path,
     *   regardless of whether it has a page or not.
     *
     * Example:
     *   Current URL: /admin/communication/messages/schedule
     *   Active items: Admin, Communication, Messages, Schedule
     */
    isSideNavActive(link: string, originalLink?: string): boolean {

        // Strip query params to only compare the actual path
        const currentUrl = this.router.url.split("?")[0];

        // Prefer originalLink (represents the logical parent path), fallback to link when it's a real page item.
        const checkLink = originalLink ? originalLink : link;

        // Ensure all paths start with "/" for consistent comparison
        const normalized = checkLink.startsWith("/") ? checkLink : "/" + checkLink;

        // Check both exact match and "startsWith" so parents light up
        // when a child is active
        return currentUrl === normalized || currentUrl.startsWith(normalized + "/");
    }
    
    asIsOrder(a, b) {
        if(a.data?.sortOrder && b.data?.sortOrder){
            return Number.parseInt(a.data.sortOrder) > Number.parseInt(b.data.sortOrder) ? 1 : -1;
        } else {
            return Number.parseInt(a.sortOrder) > Number.parseInt(b.sortOrder) ? 1 : -1;
        }
    }

    triggerMobileNav(): void {
        this.showMobileNav = true;
    }

    collapseMobileNav(): void {
        this.showMobileNav = !this.showMobileNav;
    }

    expandOrCollapse(item: any): void {
        item.collapsed = !item.collapsed;
    }

    navigate(link: string): void {
        this.router.navigate([link]);
        this.showMobileNav = false;
    }
}